import { useCallback, useSyncExternalStore } from "react";
import { type BulkEditRunStatus } from "@njbsoft/api-client";

/**
 * The runs the app-wide watcher polls, per org: each watched run id with the
 * status it was last seen at. It lives at module scope, not in a component, so
 * a watcher that unmounts and mounts again still knows a run was applying and
 * fires its transition when the next read finds it finished.
 */
const watchedByOrg = new Map<string, Map<string, BulkEditRunStatus>>();

/** Each org's watched ids, replaced only when that set of ids changes. */
const snapshots = new Map<string, readonly string[]>();

const listeners = new Set<() => void>();

const NO_RUNS: readonly string[] = [];

function publish(orgId: string): void {
  const runs = watchedByOrg.get(orgId);
  if (runs === undefined) snapshots.delete(orgId);
  else snapshots.set(orgId, [...runs.keys()]);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Watch a run the caller has just read applying. Registering records
 * `applying` as the run's last-seen status, so a first watcher read that
 * already finds it applied or failed is a transition. A run already watched
 * keeps the status it was last seen at.
 */
export function watchBulkEditRun(orgId: string, runId: string): void {
  let runs = watchedByOrg.get(orgId);
  if (runs?.has(runId)) return;
  if (runs === undefined) {
    runs = new Map();
    watchedByOrg.set(orgId, runs);
  }
  runs.set(runId, "applying");
  publish(orgId);
}

/** Stop watching a run and forget the status it was last seen at. */
export function unwatchBulkEditRun(orgId: string, runId: string): void {
  const runs = watchedByOrg.get(orgId);
  if (runs === undefined || !runs.delete(runId)) return;
  if (runs.size === 0) watchedByOrg.delete(orgId);
  publish(orgId);
}

/** The status a watched run was last seen at; undefined for a run not watched. */
export function bulkEditLastSeen(orgId: string, runId: string): BulkEditRunStatus | undefined {
  return watchedByOrg.get(orgId)?.get(runId);
}

/**
 * Record the status a watched run was just read at. A run nobody registered is
 * left unwatched: only a read of `applying` starts a watch.
 */
export function setBulkEditLastSeen(orgId: string, runId: string, status: BulkEditRunStatus): void {
  const runs = watchedByOrg.get(orgId);
  if (runs?.has(runId)) runs.set(runId, status);
}

/**
 * Forget every org's watched runs: the session ended. Cleared beside the query
 * cache on every sign-out path, so a later session in this tab never polls the
 * runs the previous one was watching.
 */
export function clearBulkEditWatches(): void {
  if (watchedByOrg.size === 0) return;
  watchedByOrg.clear();
  snapshots.clear();
  for (const listener of listeners) listener();
}

/** The org's watched run ids, one array reference per change of the set. */
export function useWatchedBulkEditRuns(orgId: string | null): readonly string[] {
  const getSnapshot = useCallback(
    () => (orgId === null ? NO_RUNS : (snapshots.get(orgId) ?? NO_RUNS)),
    [orgId],
  );
  return useSyncExternalStore(subscribe, getSnapshot);
}
